import { useProductDetails } from '@/stores/product-details'

// Adjust pricing according to plan selected.
// Optional productSource: use a vehicle ref/object instead of the productDetails store
// (e.g. order-request page) so we don't hydrate Pinia with a Vehicle model.
export const useSubscriptionPlanAndAdjustPricing = (payload, productSource) => {
	const productDetails = useProductDetails()
	const subscriptionPlan = toRef(payload)
	const term = computed(() => subscriptionPlan.value)
	const usesStoreProduct = productSource == null
	const product = computed(() =>
		usesStoreProduct ? productDetails.product : (unref(productSource) ?? {})
	)

	const validPlanTerms = [6, 9, 12]

	// Helper function to get values based on subscription duration
	const getPlanTerms = (term, key, contractKey) => {
		const plan = product.value?.service_plans?.[contractKey]
		return plan?.discount?.[key] ?? plan?.[key]
	}

	const getContractKey = (term, plans) => {
		if (!plans || typeof plans !== 'object') {
			return null
		}

		for (const [key, plan] of Object.entries(plans)) {
			if (plan.contract?.period_length === term) {
				return key // Return the key if a 12-month contract is found
			}
		}
		return null // Return null if no matching contract is found
	}

	// Get discounted upfront cost and set term duration
	const adjustedUpfrontCost = computed(() => {
		if (validPlanTerms.includes(term.value)) {
			if (usesStoreProduct) {
				productDetails.setSubscriptionDuration(term.value)
			}

			let contractKey = getContractKey(
				term.value,
				product.value?.service_plans
			)

			return getPlanTerms(term.value, 'upfront_fee', contractKey)
		} else {
			if (usesStoreProduct) {
				productDetails.setSubscriptionDuration(null)
			}
			return product.value?.discount_set?.upfront_fee
		}
	})

	// Get adjusted total minimum cost
	const adjustedTotalMinCost = computed(() => {
		if (validPlanTerms.includes(term.value)) {
			let contractKey = getContractKey(
				term.value,
				product.value?.service_plans
			)

			return getPlanTerms(term.value, 'total_min_cost', contractKey)
		} else {
			return product.value?.total_min_cost
		}
	})

	// Get discount rate from plan selected
	const planDiscount = computed(() => {
		let contractKey = getContractKey(
			term.value,
			product.value?.service_plans
		)

		return validPlanTerms.includes(term.value)
			? product.value?.service_plans?.[contractKey]?.discount
			: null
	})

	// Check if minimum term is selected.
	// Also used as a flag to display discount badge.
	const isMinTermSelected = computed(() =>
		validPlanTerms.includes(subscriptionPlan.value)
	)

	// Check if the selected plan has a valid discount
	const hasValidDiscountPlan = computed(() => {
		const discountSet = product.value?.discount_set

		// Campaign keys
		const validRefs = [
			'free_upfront_fee',
			'spring_sales',
			'end_of_year_sale',
			'always_on_sales',
			'black_friday_sale',
			'flash_sale',
		]

		// FREEUFF - Free Upfront Fee
		// HALFUFF - Half Upfront Fee
		// VOUCHERUFF - Loopit promocode auto-apply at checkout
		const validKeys = ['FREEUFF', 'HALFUFF', 'VOUCHERUFF']

		return (
			validRefs.includes(discountSet?.ref) ||
			validKeys.includes(discountSet?.key)
		)
	})

	return {
		adjustedUpfrontCost,
		adjustedTotalMinCost,
		planDiscount,
		isMinTermSelected,
		hasValidDiscountPlan,
	}
}
